import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  Optional,
  ViewEncapsulation,
  signal
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer } from '@angular/platform-browser';
import { NgxDocViewerModule } from 'ngx-doc-viewer';
import { PreviewExtension, PreviewViewerType } from './preview.model';

@Component({
  selector: 'crm-preview',
  standalone: true,
  imports: [MatIconModule, NgxDocViewerModule, MatProgressSpinnerModule],
  templateUrl: './preview.component.html',
  styleUrl: './preview.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Default
})
export class PreviewComponent {
  readonly PreviewExtension = PreviewExtension;

  /** When set, renders the viewer inline (e.g. pending-actions salary verify) instead of in a dialog. */
  @Input() inline = false;

  @Input() set documentUrl(value: string | null | undefined) {
    if (value) {
      void this.checkFileType(value);
    }
  }

  url = signal<string>('');
  type = signal<PreviewViewerType>(PreviewViewerType.URL);
  extension = signal<PreviewExtension>(PreviewExtension.NONE);
  videoType = signal<string>('');
  isLoading = signal<boolean>(false);

  // ponytail: generation token; AbortController would also cancel the fetch
  private requestToken = 0;

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) data: { url: string } | null,
    @Optional() private readonly dialogRef: MatDialogRef<PreviewComponent> | null,
    private readonly sanitizer: DomSanitizer,
    private readonly cdr: ChangeDetectorRef
  ) {
    if (data?.url) {
      void this.checkFileType(data.url);
    }
  }

  /* to check the MIME type of the file and
   * set the url to the file with appropriate viewer
   */
  async checkFileType(url: string) {
    const token = ++this.requestToken;
    const isStale = () => token !== this.requestToken;

    try {
      this.isLoading.set(true);
      const response = await fetch(url, { method: 'GET', mode: 'cors' });
      if (isStale()) return;

      const blob = await response.blob();
      if (isStale()) return;

      const fileType = blob.type;
      this.extension.set(PreviewExtension.NONE);

      if (!fileType) {
        this.type.set(PreviewViewerType.URL);
        if (this.dialogRef) {
          this.closeDialog();
        }
        return;
      }

      if (fileType.startsWith('image/')) {
        this.extension.set(PreviewExtension.IMAGE);
        this.url.set(this.sanitizer.bypassSecurityTrustResourceUrl(url) as string);
      } else if (fileType === 'application/pdf') {
        this.type.set(PreviewViewerType.URL);
        this.extension.set(PreviewExtension.PDF);
        this.url.set(this.sanitizer.bypassSecurityTrustResourceUrl(url) as string);
      } else if (
        fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        fileType === 'application/msword' ||
        fileType === 'application/vnd.oasis.opendocument.text' ||
        fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        fileType === 'application/vnd.ms-excel'
      ) {
        this.type.set(PreviewViewerType.OFFICE);
        this.url.set(url);
      } else if (fileType.startsWith('video/')) {
        this.extension.set(PreviewExtension.VIDEO);
        this.videoType.set(fileType);
        this.url.set(this.sanitizer.bypassSecurityTrustResourceUrl(url) as string);
      } else {
        this.type.set(PreviewViewerType.URL);
        this.url.set(url);
      }
    } catch {
      if (isStale()) return;
      this.extension.set(PreviewExtension.URL);
      if (this.dialogRef) {
        this.closeDialog();
      } else {
        this.url.set(url);
      }
    } finally {
      if (isStale()) return;
      if (!this.url()) {
        this.url.set(url);
        this.cdr.detectChanges();
      }
      this.isLoading.set(false);
      this.cdr.detectChanges();
    }
  }

  closeDialog(): void {
    this.dialogRef?.close();
  }

  onLoaded(): void {
    this.isLoading.set(false);
  }
}
